import { NavLink, Link } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Building2,
    ChevronLeft,
    ChevronRight,
    SlidersHorizontal,
    Hash,
    Truck,
    Tablet,
    ClipboardCheck,
    FileText,
    ScanLine,
    MapPin,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRBAC } from '@/hooks/useRBAC';
import strataworksLogo from '@/assets/strataworks-logo.png';

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
    /** Called when the user picks a nav link. Used by AppShell on mobile to close the drawer. */
    onMobileNavigate?: () => void;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
        isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
    );

export function Sidebar({ collapsed, onToggle, onMobileNavigate }: SidebarProps) {
    const { can, isRole } = useRBAC();
    const isAdminish = isRole('super_admin', 'org_admin', 'site_manager');
    const handleNav = () => onMobileNavigate?.();

    return (
        <aside
            className={cn(
                'flex flex-col h-full bg-sidebar border-r border-sidebar-border transition-all duration-200',
                collapsed ? 'w-16' : 'w-60'
            )}
        >
            {/* Logo — also serves as the "back to dashboard" link */}
            <Link
                to='/'
                onClick={handleNav}
                className='flex flex-col items-center px-4 py-4 border-b border-sidebar-border'
            >
                {collapsed ? (
                    <img
                        src={strataworksLogo}
                        alt='StrataWorks'
                        className='h-8 w-auto object-contain'
                    />
                ) : (
                    <img
                        src={strataworksLogo}
                        alt='StrataWorks'
                        className='h-16 w-auto object-contain'
                    />
                )}
            </Link>

            {/* Nav */}
            <nav className='flex-1 px-2 py-4 space-y-1 overflow-y-auto'>
                <NavLink to='/' end className={navLinkClass} onClick={handleNav}>
                    <LayoutDashboard className='h-4 w-4 shrink-0' />
                    {!collapsed && <span>Dashboard</span>}
                </NavLink>

                {can('approve:shifts') && (
                    <NavLink to='/approvals' className={navLinkClass} onClick={handleNav}>
                        <ClipboardCheck className='h-4 w-4 shrink-0' />
                        {!collapsed && <span>Approvals</span>}
                    </NavLink>
                )}

                {can('manage:org') && (
                    <NavLink to='/shift-logs' className={navLinkClass} onClick={handleNav}>
                        <FileText className='h-4 w-4 shrink-0' />
                        {!collapsed && <span>Shift Logs</span>}
                    </NavLink>
                )}

                {can('manage:org') && (
                    <NavLink to='/scan-form' className={navLinkClass} onClick={handleNav}>
                        <ScanLine className='h-4 w-4 shrink-0' />
                        {!collapsed && <span>Scan Form</span>}
                    </NavLink>
                )}

                {isAdminish && (
                    <>
                        {!collapsed && (
                            <p className='px-3 pt-4 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
                                Admin
                            </p>
                        )}
                        {can('manage:users') && (
                            <NavLink to='/admin/users' className={navLinkClass} onClick={handleNav}>
                                <Users className='h-4 w-4 shrink-0' />
                                {!collapsed && <span>Users</span>}
                            </NavLink>
                        )}
                        {can('manage:org') && (
                            <NavLink
                                to='/admin/organisation'
                                className={navLinkClass}
                                onClick={handleNav}
                            >
                                <Building2 className='h-4 w-4 shrink-0' />
                                {!collapsed && <span>Organisation</span>}
                            </NavLink>
                        )}
                        {can('manage:org') && (
                            <NavLink
                                to='/asset-register'
                                className={navLinkClass}
                                onClick={handleNav}
                            >
                                <Hash className='h-4 w-4 shrink-0' />
                                {!collapsed && <span>Consumable Register</span>}
                            </NavLink>
                        )}
                        {can('manage:org') && (
                            <NavLink to='/rigs' className={navLinkClass} onClick={handleNav}>
                                <Truck className='h-4 w-4 shrink-0' />
                                {!collapsed && <span>Rig Register</span>}
                            </NavLink>
                        )}
                        <NavLink to='/patterns' className={navLinkClass} onClick={handleNav}>
                            <MapPin className='h-4 w-4 shrink-0' />
                            {!collapsed && <span>Patterns</span>}
                        </NavLink>
                        <NavLink to='/devices' className={navLinkClass} onClick={handleNav}>
                            <Tablet className='h-4 w-4 shrink-0' />
                            {!collapsed && <span>Devices</span>}
                        </NavLink>
                        {can('manage:org') && (
                            <NavLink to='/settings' className={navLinkClass} onClick={handleNav}>
                                <SlidersHorizontal className='h-4 w-4 shrink-0' />
                                {!collapsed && <span>Settings</span>}
                            </NavLink>
                        )}
                    </>
                )}
            </nav>

            {/* Collapse toggle (user profile lives in TopBar — no duplicate here) */}
            <button
                onClick={onToggle}
                className='flex items-center justify-center h-8 border-t border-sidebar-border text-muted-foreground hover:text-foreground transition-colors'
            >
                {collapsed ? (
                    <ChevronRight className='h-4 w-4' />
                ) : (
                    <ChevronLeft className='h-4 w-4' />
                )}
            </button>
        </aside>
    );
}
